import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ArrowUpRight, Clock, Users, Gauge, Zap, ShieldCheck, MapPin } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const HERO_IMG = "/yeg-scoot-hero-4.jpg";

const FAQS = [
  { q: "Do I need experience to ride?", a: "Not at all. Every tour starts with a short safety briefing and practice loops. Our tours are perfect for complete beginners." },
  { q: "Is everything included?", a: "Yes — your e-scooter, helmet, a curated route and a friendly local guide are all included in the ticket price." },
  { q: "What's the minimum age?", a: "Riders must be 10 or older. Riders under 18 need a guardian's consent form, available at check-in." },
  { q: "What happens if it rains?", a: "Tours run rain or shine in light weather. In the case of unsafe conditions, we'll reschedule you, or issue a credit for another tour." },
  { q: "Where do tours start?", a: "The meeting point is in downtown at Off the Record Cafe (10011 - 102 Ave) across from the SW corner of Churchill Square. A second email will be sent out after the confirmation booking email with directions and parking options." },
];

const STEPS = [
  { n: "01", t: "Choose your Experience", d: "Pick from River Valley, Picnic, Murals, etc." },
  { n: "02", t: "Book & Pay Online", d: "Secure your spot in under two minutes." },
  { n: "03", t: "Show up & Scoot", d: "We handle the scooters, helmets and the route." },
];

export default function Home() {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/tours")
      .then((res) => setTours(res.data))
      .catch(() => setTours([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="App bg-white">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Riding e-scooters" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#12414d]/55" />
        </div>
        <div className="relative max-w-[1400px] mx-auto w-full px-6 md:px-12 pb-20 pt-32">
          <p className="text-white/80 font-mono text-xs md:text-sm uppercase tracking-[0.3em] mb-6" data-testid="hero-overline">
            Guided E-Scooter Tours · Edmonton, Alberta
          </p>
          <h1 className="text-white font-display font-black tracking-tighter leading-[0.85] text-6xl md:text-8xl lg:text-[8rem]" data-testid="hero-heading">
            SEE THE CITY<br />AT SCOOT SPEED<span className="text-[#4cc3c7]">.</span>
          </h1>
          <div className="flex flex-col md:flex-row md:items-center gap-6 mt-10">
            <a
              href="#tours"
              data-testid="hero-book-cta"
              className="inline-flex items-center gap-3 bg-signal text-white px-8 py-5 text-sm font-bold uppercase tracking-widest hover:bg-white hover:text-void transition-colors duration-200"
            >
              Book a Tour <ArrowUpRight size={18} />
            </a>
            <p className="text-white/70 max-w-md text-base md:text-lg">
              Small groups. Local guides. The best of Edmonton on two electric wheels.
            </p>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="bg-void text-white py-5 overflow-hidden border-y border-white/10">
        <div className="marquee-track">
          {Array(2).fill(0).map((_, i) => (
            <span key={i} className="font-display font-bold text-lg uppercase tracking-widest flex items-center">
              {["ALBERTA LEGISLATURE", "BEER STREET", "CUSTOM TOURS", "DOWNTOWN", "FUNICULAR", "HIGH LEVEL BRIDGE", "ICE DISTRICT", "MANCHESTER SQUARE", "MUTTART CONSERVATORY", "NEON MUSEUM", "O-DAY'MIN PARK", "OLD STRATHCONA", "PICNIC TOURS", "RIVERFRONT PARK", "RIVER VALLEY", "TAWATINÂ BRIDGE", "TODD LOOKOUT", "WALL MURALS", "WALTERDALE BRIDGE", "WHYTE AVENUE"].map((t) => (
                <span key={t} className="flex items-center">
                  <span className="px-8">{t}</span>
                  <span className="text-signal">/</span>
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>

      {/* TOURS */}
      <section id="tours" className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-32 scroll-mt-20">
        <div className="grid md:grid-cols-12 items-end gap-8 mb-16">
          <div className="md:col-span-8">
            <p className="text-signal font-mono text-xs uppercase tracking-[0.3em] mb-4">Choose your ride</p>
            <h2 className="font-display font-bold tracking-tighter leading-none text-4xl md:text-6xl">
              Tours built<br />for Edmonton<span className="text-[#4cc3c7]">.</span>
            </h2>
          </div>
          <p className="md:col-span-4 text-muted-foreground text-lg">
            Every tour includes a brand-new e-scooter, helmet, safety briefing and a local guide who is a certified <span className="text-[#4cc3c7]">YEG EXPERT</span>  from <a href="https://exploreedmonton.com" target="_blank" rel="noreferrer" className="underline hover:text-[#4cc3c7] transition-colors">Explore Edmonton</a>.
          </p>
        </div>

        {loading ? (
          <div className="py-20 text-center font-mono text-muted-foreground" data-testid="tours-loading">Loading tours…</div>
        ) : (
          <div className="grid md:grid-cols-2 border-t border-l border-border" data-testid="tours-grid">
            {tours.map((tour) => (
              <Link
                key={tour.id}
                to={`/book/${tour.slug}`}
                data-testid={`tour-card-${tour.slug}`}
                className="group relative border-b border-r border-border p-8 md:p-10 hover:bg-secondary transition-colors duration-200"
              >
                <div className="aspect-[16/10] overflow-hidden mb-8 bg-secondary">
                  <img
                    src={tour.image}
                    alt={tour.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display font-semibold text-2xl md:text-3xl tracking-tight">{tour.name}</h3>
                    <p className="text-muted-foreground mt-1">{tour.tagline}</p>
                  </div>
                  <ArrowUpRight size={28} className="shrink-0 text-void group-hover:text-signal transition-colors duration-200" />
                </div>
                <div className="flex flex-wrap items-center gap-5 mt-6 font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  <span className="flex items-center gap-2"><Clock size={14} /> {tour.duration}</span>
                  <span className="flex items-center gap-2"><Gauge size={14} /> {tour.difficulty}</span>
                  <span className="flex items-center gap-2"><Users size={14} /> Max {tour.max_group}</span>
                </div>
                <div className="mt-8 flex items-baseline gap-2">
                  <span className="font-display font-black text-3xl tracking-tighter">${tour.price.toFixed(0)}</span>
                  <span className="text-muted-foreground text-sm">CAD / person</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* ABOUT / WHY */}
      <section id="about" className="bg-void text-white scroll-mt-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-32">
          <div className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-5">
              <p className="text-signal font-mono text-xs uppercase tracking-[0.3em] mb-4">Why ride with us</p>
              <h2 className="font-display font-bold tracking-tighter leading-none text-4xl md:text-6xl">
                Locals<span className="text-[#4cc3c7]">.</span><br />Not a<br />guidebook<span className="text-[#4cc3c7]">.</span>
              </h2>
            </div>
            <div className="md:col-span-7 grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
              {[
                { icon: <Zap />, t: "All-Electric Fleet", d: "Brand-new, well-maintained scooters with plenty of range for the full route." },
                { icon: <ShieldCheck />, t: "Safety First", d: "Helmets, briefings and certified guides on every ride." },
                { icon: <MapPin />, t: "Hidden Gems", d: "Routes that highlight many of the locals' favorites." },
                { icon: <Users />, t: "Small Groups", d: "Intimate group sizes so you actually get to know your guide." },
              ].map((f) => (
                <div key={f.t} className="bg-void p-8">
                  <div className="text-signal mb-5">{f.icon}</div>
                  <h3 className="font-display font-semibold text-xl">{f.t}</h3>
                  <p className="text-white/60 mt-2">{f.d}</p>
                </div>
              ))}
            </div>
          </div>

          {/* STEPS */}
          <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10 mt-px">
            {STEPS.map((s) => (
              <div key={s.n} className="bg-void p-8 md:p-10">
                <span className="font-mono text-signal text-sm">{s.n}</span>
                <h3 className="font-display font-semibold text-2xl mt-4">{s.t}</h3>
                <p className="text-white/60 mt-2">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-32 scroll-mt-20">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <p className="text-signal font-mono text-xs uppercase tracking-[0.3em] mb-4">Good to know</p>
            <h2 className="font-display font-bold tracking-tighter leading-none text-4xl md:text-5xl">
              Frequently<br />asked<span className="text-[#4cc3c7]">.</span>
            </h2>
          </div>
          <div className="md:col-span-8">
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-border">
                  <AccordionTrigger className="text-left font-display font-semibold text-lg md:text-xl py-6 hover:no-underline hover:text-signal">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
